import { useFeatureGate } from '@atlassian/feature-gates-react';

import type { AwsWafConfig } from '../types';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { useScript } from '../utilities/use-script';
import { AWS_CAPTCHA_SCRIPT_ID } from './aws-captcha.constants';

type UseAwsWafCaptchaSdkScriptReturnType = {
  /** True once the AWS WAF CAPTCHA SDK script has finished loading. */
  hasLoaded: boolean;
};

/**
 * Loads the CAPTCHA SDK when configured, using both same-origin SDKs when their gate is enabled,
 * and reports whether the CAPTCHA SDK has finished loading.
 *
 * Injection and readiness are delegated to `useScript` from `usehooks-ts`, which
 * de-duplicates by `src` (with the tag keyed on {@link AWS_CAPTCHA_SCRIPT_ID})
 * and shares loading status across consumers via an internal cache; remounting
 * this hook therefore cannot append a duplicate script. The script is left in
 * place after unmount (`removeOnUnmount` defaults to false) — it is a page-level
 * dependency and does not need to be loaded twice.
 *
 * If the SDK global is already present (e.g. the script loaded on a previous
 * render before this hook mounted), `hasLoaded` reflects that synchronously.
 */
export const useAwsWafCaptchaSdkScript = (
  awsWafConfig: AwsWafConfig | null
): UseAwsWafCaptchaSdkScriptReturnType => {
  const useSameOriginSdks = useFeatureGate(AidFrontendFeatureGate.ENABLE_SAME_ORIGIN_AWS_WAF_SDKS);
  const challengeSdkUrl = `${window.location.origin}/awswaf-challenge/challenge.js`;
  const captchaSdkUrl = useSameOriginSdks
    ? `${window.location.origin}/awswaf-captcha/jsapi.js`
    : awsWafConfig?.integrationUrl ?? null;

  // Ordered dynamic scripts execute challenge first, even if CAPTCHA downloads first.
  useScript(awsWafConfig && useSameOriginSdks ? challengeSdkUrl : null, {
    async: false,
    defer: true,
  });
  const status = useScript(awsWafConfig ? captchaSdkUrl : null, {
    id: AWS_CAPTCHA_SCRIPT_ID,
    async: !useSameOriginSdks,
    defer: useSameOriginSdks,
  });

  // The SDK global is defined by the script once it executes; treat its presence
  // as "loaded" to cover the case where the script resolved before this hook
  // subscribed to the `load` event.
  const hasLoaded = status === 'ready' || Boolean(window.AwsWafCaptcha);

  return { hasLoaded };
};
